// Estimates answered line by line (Customer Portal v3 2e, docs/portal-v3-plan.md session 3). Pure
// rules shared by the portal (portal.js), the office (estimates.js) and the server
// (respondToEstimate, functions/index.js via functions/shared).
//
// An estimate line (Operations[i]) may offer choices: Options: [{ Label, Amount }] (e.g. Rebuild /
// Replace); the customer then picks one or "Not now". The answer is kept on the line:
//   Customer_Decision  'approved' | 'declined'
//   Option             the Label chosen (lines with Options), Option_Amount its price
// A decision for line i is 'approved', 'declined', or 'opt:N' (the Nth option).

import { toCents, toDollars, sumCents, lineCents, priceTotals } from './money.js';

/** The lines the customer answers: the estimate's operations that have a description. */
export const answerableLines = ops => (ops || []).map((op, i) => ({ op, i })).filter(x => x.op && x.op.Description);

/** The choices for a line: [{ key, label, amount }]. Plain lines: Approve / Decline. */
export function lineChoices(op) {
  const opts = (op?.Options || []).filter(o => o && o.Label);
  if (!opts.length) return [{ key: 'approved', label: 'Approve', amount: Number(op?.Amount) || 0 }, { key: 'declined', label: 'Decline', amount: 0 }];
  return [{ key: 'declined', label: 'Not now', amount: 0 }, ...opts.map((o, n) => ({ key: `opt:${n}`, label: String(o.Label), amount: Number(o.Amount) || 0 }))];
}

/** What a decision costs for a line (0 when declined or not a valid choice). */
export function lineAmount(op, decision) {
  const c = lineChoices(op).find(x => x.key === decision);
  return c ? c.amount : 0;
}

/**
 * Check the customer's answers. decisions: { [lineIndex]: decision }. Every answerable line needs one.
 * Returns { ok, error?, lines: [{ i, decision, approved, option, amount }] }.
 */
export function checkDecisions(ops, decisions) {
  const lines = [];
  for (const { op, i } of answerableLines(ops)) {
    const d = decisions?.[i];
    const choice = lineChoices(op).find(x => x.key === d);
    if (!choice) return { ok: false, error: 'Approve or decline each item.', lines: [] };
    const approved = d !== 'declined';
    lines.push({ i, decision: d, approved, option: d.startsWith('opt:') ? choice.label : null, amount: approved ? choice.amount : 0 });
  }
  if (!lines.length) return { ok: false, error: 'This estimate has no items to answer.', lines: [] };
  return { ok: true, lines };
}

/**
 * The estimate's price for what was approved: approved labor + the parts (parts are for the whole
 * job, so they count once anything is approved), then the estimate's own fee, tax and deposit rates.
 * Same shape as the estimate's Pricing (dollars).
 */
export function approvedPricing(est, lines) {
  const p = est?.Pricing || {};
  const any = lines.some(l => l.approved);
  const laborCents = sumCents(lines.filter(l => l.approved).map(l => toCents(l.amount)));
  const partsCents = any ? (est?.Parts?.length ? sumCents(est.Parts.map(x => lineCents(x.Qty, toCents(x.Unit_Price)))) : toCents(p.partsTotal)) : 0;
  const t = priceTotals({ subtotal: laborCents + partsCents, taxRate: Number(p.salesTaxRate) || 0, feeRate: Number(p.environmentalFeeRate) || 0, depositPercent: Number(p.depositPercent) || 0 });
  return {
    laborTotal: toDollars(laborCents), partsTotal: toDollars(partsCents),
    environmentalFeeRate: Number(p.environmentalFeeRate) || 0, environmentalFee: toDollars(t.fee),
    salesTaxRate: Number(p.salesTaxRate) || 0, salesTax: toDollars(t.tax),
    total: toDollars(t.total), depositPercent: Number(p.depositPercent) || 0, depositDue: toDollars(t.deposit),
  };
}

/** The line as the office reads it after the customer answered: 'Approved', 'Declined', 'Chose Replace'. */
export function decisionLabel(op) {
  if (op?.Customer_Decision === 'declined') return 'Declined';
  if (op?.Customer_Decision === 'approved') return op.Option ? `Chose ${op.Option}` : 'Approved';
  return '';
}
